import { useState, useRef, type FormEvent } from 'react';

export default function NewsletterSection() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('website_alt') as string || '').trim();
    if (honeypotValue) {
      setStatus('success');
      setMessage('Thank you for subscribing!');
      form.reset();
      return;
    }

    setStatus('loading');
    setMessage('');

    try {
      const res = await fetch('https://readdy.ai/api/form/d9ov4caftmtnfcfh0ek0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setStatus('success');
        setMessage('Thank you for subscribing! Check your inbox for updates.');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || 'Something went wrong. Please try again.';
        setStatus('error');
        setMessage(serverMsg);
      }
    } catch {
      setStatus('error');
      setMessage('Network error. Please check your connection and try again.');
    }
  };

  return (
    <section className="py-16 md:py-20 bg-background-50">
      <div className="max-w-2xl mx-auto px-4 md:px-6 text-center">
        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mx-auto mb-4">
          <i className="ri-mail-send-line text-xl"></i>
        </div>
        <h2 className="text-2xl font-heading font-semibold text-foreground-900 mb-2">
          Stay Updated with <strong>Sugargoo Spreadsheet</strong>
        </h2>
        <p className="text-sm text-foreground-600 mb-6 max-w-md mx-auto">
          Subscribe to receive the latest <strong>Sugargoo Spreadsheet</strong> updates, 
          new product finds, and exclusive buying tips directly in your inbox.
        </p>

        <form ref={formRef} onSubmit={handleSubmit} data-readdy-form>
          <div className="flex flex-col sm:flex-row gap-2 max-w-md mx-auto">
            <input
              type="email"
              name="email"
              required
              placeholder="Your email address"
              className="flex-1 px-4 py-2.5 text-sm border border-background-200/70 rounded-md bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400/50 focus:border-primary-300"
            />
            <button
              type="submit"
              disabled={status === 'loading'}
              className="px-5 py-2.5 text-sm font-semibold bg-primary-500 text-white rounded-md hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              {status === 'loading' ? (
                <span className="flex items-center gap-1">
                  <i className="ri-loader-4-line animate-spin"></i> Subscribing...
                </span>
              ) : (
                'Subscribe'
              )}
            </button>
          </div>

          <div className="anti-spam-hidden">
            <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
          </div>

          {message && (
            <p className={`mt-3 text-xs ${status === 'success' ? 'text-primary-700' : 'text-accent-600'}`}>
              {message}
            </p>
          )}
        </form>
      </div>
    </section>
  );
}